import { html } from 'lit';
import {
  CellContext,
  ColumnDefTemplate,
  HeaderContext,
} from '@tanstack/table-core';
import {
  property,
  queryAssignedElements,
  queryAssignedNodes,
  state,
} from 'lit/decorators.js';
import { tabbable, FocusableElement } from 'tabbable';
import { pdsCustomElement as customElement } from '../../decorators/pds-custom-element';
import { PdsElement } from '../PdsElement';
import styles from './data-table-input-column.scss?inline';
import { required } from '../../decorators/required';
import { PdsTextInput } from '../text-input/text-input';
import { PdsSelect } from '../select/select';
import type { PdsDataTable } from '../data-table/data-table';

/**
 * @summary This component supports data table by providing an interface to define table columns with an input
 *
 * @slot default Provides an interface to define column header markup and row cells
 */
@customElement('pds-data-table-input-column', {
  category: 'component',
  type: 'component',
  styles,
})
export class PdsDataTableInputColumn extends PdsElement {
  protected firstUpdated() {
    this.header = this.getHeader();

    if (document) {
      // track state of shift key for tabbing
      document.body.addEventListener('keydown', (event) => {
        if (event.key === 'Shift') {
          this.shiftKeyPressed = true;
        } else if (event.key === 'Tab') {
          this.tabKeyPressed = true;
        }
      });
      document.body.addEventListener('keyup', (event) => {
        if (event.key === 'Shift') {
          this.shiftKeyPressed = false;
        } else if (event.key === 'Tab') {
          this.tabKeyPressed = false;
        }
      });

      // track clicked item for blur
      document.body.addEventListener('mousedown', (event) => {
        this.elementBeingClicked = event.composedPath()[0] as FocusableElement;
      });
      document.body.addEventListener('mouseup', () => {
        this.elementBeingClicked = null;
      });
    }
  }

  /**
   * columnId
   * - id used to identify column, required
   */
  @required
  @property({ type: String })
  columnId: string;

  /**
   * dataSyncId
   * - id used to identify link between input and table data that is manipulated with the input, required
   */
  @required
  @property({ type: String })
  dataSyncId: string;

  /**
   * inputLabel
   * - string used to label the input for accessibility, otherwise will use text from the slot
   */
  @property({ type: String })
  inputLabel: string;

  /**
   * Input type
   * - **text** Text input
   */
  @property({ type: String })
  inputType: 'text' = 'text';

  /**
   * Type
   * - **display** column does not have a data model which means it cannot be sorted, filtered, etc, but they can be used to display arbitrary content in the table, eg. a row actions button, checkbox, expander, etc.
   */
  @property({ type: String })
  type: 'display' = 'display';

  /**
   * Align
   * - **left** align left, default
   * - **center** align center
   * - **right** align right
   */
  @property({ type: String })
  align: 'left' | 'center' | 'right' = 'left';

  /**
   * Width
   * - css width value string
   */
  @property({ type: String })
  width: string;

  /**
   * Disable sort
   * - boolean to determine if column sorting should be disabled, default is true
   */
  @property({ type: Boolean })
  disableSort: boolean = true;

  /**
   * Cell
   * - takes a function to return markup for displaying the body cell for the column
   * - defaults to function that handles wiring up data changes from a PDS text input or PDS select if one is slotted in, or creates a wired up PDS text input
   *
   */
  @property()
  cell: ColumnDefTemplate<CellContext<any, unknown>> = (cell) => {
    const initialValue: string = cell.row.getValue(this.dataSyncId);
    let value = initialValue;
    let inputEl;

    // When the input is blurred, we'll set the table data
    const onBlur = async (e: Event) => {
      if (cell.table.options.data[cell.row.index][this.dataSyncId] !== value) {
        // eslint-disable-next-line no-param-reassign
        cell.table.options.data[cell.row.index][this.dataSyncId] = value;

        // fire custom event
        const event = new CustomEvent('pds-data-table-input-updated', {
          bubbles: true,
          composed: true,
          detail: {
            rowIndex: cell.row.index,
            column: this.dataSyncId,
            value,
          },
        });

        this.dispatchEvent(event);

        // if we are showing the column that we are updating in the table, we need to refresh the table
        // @ts-expect-error
        if (!cell.table.getColumn(this.dataSyncId).columnDef.meta.hidden) {
          const eventTarget = e.target as HTMLElement;
          const dataTableWrapper = eventTarget.closest(
            '.pds-c-data-table__wrapper',
          );
          const currentNode = eventTarget.shadowRoot?.querySelector('input');

          if (dataTableWrapper && currentNode) {
            let currentlyTabbableItems = tabbable(dataTableWrapper, {
              getShadowRoot: true,
            });
            let clickedItem;

            // get next and prev tabbable item
            const nextTabbableItem =
              currentlyTabbableItems.indexOf(currentNode) + 1;
            const previousTabbableItem =
              currentlyTabbableItems.indexOf(currentNode) - 1;

            // get clicked item in table
            if (this.elementBeingClicked) {
              clickedItem = currentlyTabbableItems.indexOf(
                this.elementBeingClicked,
              );
            }

            await (
              this.closest('pds-data-table') as PdsDataTable
            ).createTable();
            await this.updateComplete;
            // need to repopulate the tabbable items after table refresh
            currentlyTabbableItems = tabbable(dataTableWrapper, {
              getShadowRoot: true,
            });

            // set correct focus when leaving field
            if (
              this.tabKeyPressed &&
              this.shiftKeyPressed &&
              currentlyTabbableItems[previousTabbableItem]
            ) {
              currentlyTabbableItems[previousTabbableItem].focus();
            } else if (
              this.tabKeyPressed &&
              currentlyTabbableItems[nextTabbableItem]
            ) {
              currentlyTabbableItems[nextTabbableItem].focus();
            } else if (typeof clickedItem === 'number') {
              currentlyTabbableItems[clickedItem].focus();
            }
          }
        }
      }
    };

    const setValue = (e: Event) => {
      const tagName = (e.target as HTMLElement).tagName.toLowerCase();
      let target;
      if (tagName === 'pds-text-input') {
        target = e.target as PdsTextInput;
        if (target) {
          target.setAttribute('value', target.value);
          value = target.value;
        }
      } else if (tagName === 'pds-select') {
        target = e.target as PdsSelect;
        if (target) {
          target.setAttribute('value', target.value);
          value = target.value;
        }
      }
    };

    if (this.inputTypeSlotted === 'pds-text-input') {
      inputEl = this.customInput.cloneNode(true) as HTMLElement;
      inputEl.setAttribute('value', initialValue);
      inputEl.addEventListener('blur', onBlur);
      inputEl.addEventListener('pds-text-input-change', setValue);
    } else if (this.inputTypeSlotted === 'pds-select') {
      inputEl = this.customInput.cloneNode(true) as HTMLElement;
      inputEl.setAttribute('value', initialValue);
      inputEl.addEventListener('pds-select-blur', onBlur);
      inputEl.addEventListener('pds-select-change', setValue);
    } else {
      inputEl = document.createElement('pds-text-input') as PdsTextInput;
      inputEl.setAttribute(
        'label',
        this.inputLabel || this.getDynamicSlotText(),
      );
      inputEl.setAttribute('hideLabel', 'true');
      inputEl.setAttribute('size', 'sm');
      inputEl.setAttribute('value', initialValue);
      inputEl.addEventListener('blur', onBlur);
      inputEl.addEventListener('pds-text-input-change', setValue);
    }

    return inputEl;
  };

  /**
   * Header
   */
  @state()
  header: ColumnDefTemplate<HeaderContext<any, unknown>> | string | undefined;

  /**
   * @internal
   */
  @state()
  inputTypeSlotted: string | undefined;

  /**
   * @internal
   */
  @state()
  private customInput: HTMLElement;

  /**
   * @internal
   */
  @state()
  shiftKeyPressed: boolean = false;

  /**
   * @internal
   */
  @state()
  tabKeyPressed: boolean = false;

  /**
   * @internal
   */
  @state()
  elementBeingClicked: FocusableElement | null;

  /**
   * This grabs the slotted components
   * @internal
   */
  @queryAssignedNodes()
  slotElements: Array<Node>;

  /**
   * This grabs the slotted components
   * @internal
   */
  @queryAssignedElements({ slot: 'input' })
  slotInputElements: Array<HTMLElement>;

  /**
   * @internal
   */
  getDynamicSlotText() {
    let text = '';
    this.getDynamicSlotMarkup()?.forEach((element: any) => {
      text += element.wholeText ? element.wholeText : element.textContent;
      text += ' ';
    });
    return text.trim();
  }

  getHeader() {
    return this.inputLabel
      ? this.inputLabel
      : () => {
          return this.getDynamicSlotMarkup();
        };
  }

  setCustomInput() {
    if (this.slotInputElements.length > 1) {
      console.error(
        'Only one input element may be slotted into the input slot',
        this,
      );
    }
    if (this.slotInputElements[0].tagName.toLowerCase() === 'pds-select') {
      [this.customInput] = this.slotInputElements;
      this.inputTypeSlotted = this.slotInputElements[0].tagName.toLowerCase();
    } else if (
      this.slotInputElements[0].tagName.toLowerCase() === 'pds-text-input'
    ) {
      [this.customInput] = this.slotInputElements;
      this.inputTypeSlotted = this.slotInputElements[0].tagName.toLowerCase();
    } else {
      this.inputTypeSlotted = undefined;
    }
  }

  render() {
    return html`<slot
        id="slot"
        @slotchange=${(e: Event) =>
          this.duplicateSlotMarkup(e.target as HTMLSlotElement)}
      ></slot>
      <slot name="input" @slotchange=${this.setCustomInput}></slot>`;
  }
}
